import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated'

export function useSimpleRangeBulletChart(root) {
    let chart
    let yAxis
    let xAxis
    let dumbbellSeries
    let targetSeries
    let averageSeries
    let patentDatesSeries

    const configureSimpleRangeBullet = () => {
        // Clear any existing chart
        root.container.children.clear()

        root.setThemes([am5themes_Animated.new(root)])
        root.utc = true
        root.dateFormatter.setAll({
            dateFormat: 'MM-dd-yyyy',
            dateFields: ['valueX', 'openValueX', 'date', 'target', 'average'],
        })

        // Create chart with explicit dimensions
        chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                panX: false,
                panY: false,
                wheelX: 'none',
                wheelY: 'none',
                layout: root.verticalLayout,
                height: am5.percent(100),
                width: am5.percent(100),
            })
        )
    }

    const generateAxes = (mappedData) => {
        let minDate = new Date()
        let maxDate = new Date(0)

        mappedData.forEach((item) => {
            const entryDate = new Date(item.entry)
            const latestDate = new Date(item.latest)

            if (entryDate < minDate) minDate = entryDate
            if (latestDate > maxDate) maxDate = latestDate
        })

        const minYear = minDate.getFullYear() - 1
        const maxYear = maxDate.getFullYear() + 1

        const bufferedMinDate = new Date(minYear, 0, 1)
        const bufferedMaxDate = new Date(maxYear, 11, 31)

        // Create Y-axis (category axis)
        let yRenderer = am5xy.AxisRendererY.new(root, {
            minGridDistance: 30,
        })

        // Make grid lines visible
        yRenderer.grid.template.setAll({
            visible: false,
            strokeOpacity: 0.1,
        })

        // Make labels visible
        yRenderer.labels.template.setAll({
            fill: am5.color('#5D6D78'),
            fontSize: 14,
            visible: false,
        })

        yAxis = chart.yAxes.push(
            am5xy.CategoryAxis.new(root, {
                categoryField: 'name',
                renderer: yRenderer,
                visible: false,
            })
        )

        // Create X-axis (date axis)
        let xRenderer = am5xy.AxisRendererX.new(root, {
            minGridDistance: 40,
            cellStartLocation: 0,
            cellEndLocation: 1,
        })

        xRenderer.grid.template.setAll({
            visible: false,
        })

        xRenderer.labels.template.setAll({
            visible: true,
            fill: am5.color('#5D6D78'),
            fontSize: 12,
            centerX: 0,
            dx: -10,
            location: 0,
        })

        xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: { timeUnit: 'day', count: 1 },
                renderer: xRenderer,
                tooltip: am5.Tooltip.new(root, {}),
                extraMin: 0.03,
                extraMax: 0.03,
                visible: true,
                dateFormats: {
                    day: 'yyyy',
                    week: 'yyyy',
                    month: 'yyyy',
                    year: 'yyyy',
                },
                periodChangeDateFormats: {
                    day: 'yyyy',
                    week: 'yyyy',
                    month: 'yyyy',
                    year: 'yyyy',
                },
                min: bufferedMinDate.getTime(),
                max: bufferedMaxDate.getTime(),
                groupData: false,
                // groupCount: 366,
                gridIntervals: [{ timeUnit: 'year', count: 1 }],
                startLocation: 0,
                endLocation: 1,
                markUnitChange: true,
            })
        )

        xAxis.labelsContainer.set('paddingLeft', 0)
        xAxis.get('renderer').labels.template.setAll({
            paddingLeft: 0,
            textAlign: 'left',
            location: 0,
        })

        xAxis.get('renderer').labels.template.adapters.add('x', function (x, target) {
            // Get the date value for this label
            const dataItem = target.dataItem
            if (dataItem) {
                // Get the date this label represents
                const date = new Date(dataItem.get('value'))
                // If it's January 1st, position at the start
                if (date.getMonth() === 0 && date.getDate() === 1) {
                    return 0
                }
            }
            return x
        })

        // Set data for y-axis
        yAxis.data.setAll(mappedData)
    }

    const generateSeries = (mappedData) => {
        generateDumbbellSeries(mappedData)
        generateTargetSeries(mappedData)
        generateAverageSeries(mappedData)
        generatePatentDatesSeries(mappedData)
    }

    const generateDumbbellSeries = (mappedData) => {
        // Create dumbbell series
        dumbbellSeries = chart.series.push(
            am5xy.ColumnSeries.new(root, {
                name: 'First/Last Filed Patent',
                xAxis: xAxis,
                yAxis: yAxis,
                openValueXField: 'entry',
                valueXField: 'latest',
                categoryYField: 'name',
                fill: am5.color('#D6DDEB'),
            })
        )

        // Style the columns
        dumbbellSeries.columns.template.setAll({
            height: 3,
            cornerRadiusTL: 0,
            cornerRadiusTR: 0,
            cornerRadiusBL: 0,
            cornerRadiusBR: 0,
            strokeWidth: 0,
            fillOpacity: 1,
        })

        // Add bullet for start date
        dumbbellSeries.bullets.push(function () {
            return am5.Bullet.new(root, {
                locationX: 0,
                sprite: am5.Circle.new(root, {
                    fill: am5.color('#D6DDEB'),
                    radius: 8,
                    tooltipText: `[bold]Earliest Filing Date:[/] {openValueX}`,
                    tooltipY: -4,
                }),
            })
        })

        // Add bullet for end date
        dumbbellSeries.bullets.push(function () {
            return am5.Bullet.new(root, {
                locationX: 1,
                sprite: am5.Circle.new(root, {
                    fill: am5.color('#D6DDEB'),
                    radius: 8,
                    tooltipText: `[bold]Latest Filing Date:[/] {valueX}`,
                    tooltipY: -4,
                }),
            })
        })

        dumbbellSeries.data.setAll(mappedData)
        dumbbellSeries.appear(1000)
    }

    const generateTargetSeries = (mappedData) => {
        targetSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'Target',
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: 'target',
                categoryYField: 'name',
                stroke: am5.color('#FF5722'),
                tooltip: am5.Tooltip.new(root, {
                    labelText: '{valueX}',
                }),
            })
        )

        // Add bullet for target
        targetSeries.bullets.push(function () {
            return am5.Bullet.new(root, {
                sprite: am5.Circle.new(root, {
                    fill: am5.color('#F2AF3A'),
                    radius: 8,
                    tooltipText: '{valueX}',
                    tooltipY: -4,
                }),
            })
        })

        targetSeries.data.setAll(mappedData)
        targetSeries.appear(1000)
    }

    const generateAverageSeries = (mappedData) => {
        averageSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'Average',
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: 'average',
                categoryYField: 'name',
                stroke: am5.color('#4CAF50'),
                tooltip: am5.Tooltip.new(root, {
                    labelText: '[bold]Average Filing Date:[/] {valueX}',
                }),
            })
        )

        // Add bullet for average
        averageSeries.bullets.push(function () {
            return am5.Bullet.new(root, {
                sprite: am5.Rectangle.new(root, {
                    fill: am5.color('#2A79D2'), // Castle Moat
                    width: 4,
                    height: 20,
                    centerX: am5.p50,
                    centerY: am5.p50,
                    tooltipText: '[bold]Average Filing Date:[/] {valueX}',
                }),
            })
        })

        averageSeries.data.setAll(mappedData)
        averageSeries.appear(1000)
    }

    const generatePatentDatesSeries = (mappedData) => {
        if (!mappedData[0]?.patentDates || mappedData[0].patentDates.length === 0) {
            return
        }

        patentDatesSeries = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'Patent Dates',
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: 'date',
                categoryYField: 'name',
                visible: false,
            })
        )

        patentDatesSeries.strokes.template.set('forceHidden', true)

        // Add bullet for each patent date
        patentDatesSeries.bullets.push(function () {
            return am5.Bullet.new(root, {
                sprite: am5.Circle.new(root, {
                    fill: am5.color('#081728'), // Dungenon Depths
                    radius: 4,
                    tooltipText: '{valueX}',
                    tooltipY: -4,
                }),
            })
        })

        // Create data for the patent dates series
        const patentDatesData = []
        mappedData.forEach((item) => {
            if (item.patentDates) {
                item.patentDates.forEach((patentDate) => {
                    patentDatesData.push({
                        name: item.name,
                        date: patentDate.date,
                        id: patentDate.id,
                    })
                })
            }
        })

        patentDatesSeries.data.setAll(patentDatesData)
        patentDatesSeries.appear(1000)
    }

    const disposeRangeBullet = () => {
        if (chart) {
            chart.dispose()
        }
    }

    return {
        configureSimpleRangeBullet,
        generateAxes,
        generateSeries,
        disposeRangeBullet,
    }
}
